import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
  Table,
  Form,
  Button,
  Row,
  Col,
  Pagination,
  Container,
  Spinner,
  Badge,
} from 'react-bootstrap';
import { supabase } from '../../lib/supabase';
import type {
  QuestionnaireTemplate,
  UserOrganisation,
} from '../../types/types';
import NewQuestionnaireModal from './NewQuestionnaireModal';
import { captureSupabaseError } from '../../lib/sentry';
import { tracking } from '../../util/tracking';

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

export default function QuestionnaireList({
  reloadKey,
  onSendToSupplier,
}: {
  reloadKey: number;
  onSendToSupplier: (templateId: string) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQueryParams();

  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const [search, setSearch] = useState(query.get('search') || '');
  const [page, setPage] = useState(Number(query.get('page') || 1));
  const [sortBy, setSortBy] = useState<'title' | 'updated_at' | 'status'>(
    (query.get('sortBy') as 'title' | 'updated_at' | 'status') || 'updated_at',
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    (query.get('sortDirection') as 'asc' | 'desc') || 'desc',
  );
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [showNewQuestionnaireModal, setShowNewQuestionnaireModal] =
    useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireTemplate[]>(
    [],
  );
  const [total, setTotal] = useState(0);

  const pageSize = 10;

  // Sync query params with state
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (page > 1) params.set('page', String(page));
    if (sortBy) params.set('sortBy', sortBy);
    if (sortDirection) params.set('sortDirection', sortDirection);

    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
  }, [search, page, sortBy, sortDirection]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      if (search) setPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch questionnaires
  useEffect(() => {
    if (!selectedOrganisation?.organisation?.id) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        let query = supabase
          .from('questionnaire_templates')
          .select('*', { count: 'exact' })
          .eq('organisation_id', selectedOrganisation?.organisation?.id);

        if (debouncedSearch) {
          query = query.ilike('title', `%${debouncedSearch}%`);
        }

        query = query.order(sortBy, { ascending: sortDirection === 'asc' });
        query = query.range((page - 1) * pageSize, page * pageSize - 1);

        const { data, error, count } = await query;

        if (error) throw error;

        setQuestionnaires(data ?? []);
        setTotal(count ?? 0);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        captureSupabaseError(err, { op: 'questionnaires.fetch' });
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    selectedOrganisation?.organisation?.id,
    reloadKey,
    debouncedSearch,
    page,
    sortBy,
    sortDirection,
  ]);

  const totalPages = Math.ceil(total / pageSize);

  const handleSort = (column: 'title' | 'updated_at' | 'status') => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column: 'title' | 'updated_at' | 'status') => {
    if (sortBy !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  if (!selectedOrganisation?.organisation) {
    return <div>Please select an organisation to view questionnaires.</div>;
  }

  return (
    <div>
      <style>
        {`
          .questionnaire-list-table > :not(caption) > * > * {
            padding-top: 0.9rem;
            padding-bottom: 0.9rem;
            vertical-align: middle;
          }

          .questionnaire-list-table > tbody > tr > td:not(:first-child) {
            border-left-width: 0;
          }

          .questionnaire-list-table > tbody > tr > td:not(:last-child) {
            border-right-width: 0;
          }

          .questionnaire-list-table > thead > tr > th:not(:first-child) {
            border-left-width: 0;
          }

          .questionnaire-list-table > thead > tr > th:not(:last-child) {
            border-right-width: 0;
          }
        `}
      </style>
      <Row>
        <Col>
          <Button
            className="ms-2"
            variant="primary"
            onClick={() => {
              tracking.log('UI', 'questionnaire_new', {
                page: 'questionnaires',
              });
              setShowNewQuestionnaireModal(true);
            }}
          >
            + New Questionnaire
          </Button>
        </Col>
      </Row>

      {loading && (
        <Container fluid className="d-flex justify-content-center py-5">
          <Spinner animation="border" variant="primary" />
        </Container>
      )}

      {error && <div className="text-danger">Error: {error}</div>}

      {!loading && (
        <>
          <div
            className={`border bg-white p-3 mt-3 ${
              questionnaires.length > 0
                ? 'border-bottom-0 rounded-top-3 rounded-bottom-0'
                : 'rounded-3'
            }`}
          >
            <Row className="g-3 align-items-end">
              <Col md={5} className="ms-md-auto">
                <Form.Group className="mb-0" controlId="questionnaireSearch">
                  <Form.Control
                    type="text"
                    placeholder="Search questionnaires..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {questionnaires.length === 0 ? (
            <div>No questionnaires found.</div>
          ) : (
            <>
              <Table
                bordered
                hover
                responsive
                className="mb-0 questionnaire-list-table"
              >
                <thead>
                  <tr>
                    <th
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('title')}
                    >
                      Title {renderSortIcon('title')}
                    </th>
                    <th>Description</th>
                    <th
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('status')}
                    >
                      Status {renderSortIcon('status')}
                    </th>
                    <th>Actions</th>
                    <th>Version</th>
                    <th
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('updated_at')}
                    >
                      Updated {renderSortIcon('updated_at')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {questionnaires.map((q) => (
                    <tr
                      key={q.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        navigate(`/questionnaires/${q.id}/edit`, {
                          state: { from: location },
                        })
                      }
                    >
                      <td>{q.title}</td>
                      <td>{q.description || '-'}</td>
                      <td>
                        <Badge
                          bg={
                            q.status === 'draft'
                              ? 'secondary'
                              : q.status === 'published'
                                ? 'success'
                                : 'light'
                          }
                        >
                          {q.status}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            tracking.log('UI', 'questionnaire_send_open', {
                              templateId: q.id,
                              page: 'questionnaires_list',
                            });
                            onSendToSupplier(q.id);
                          }}
                        >
                          Send
                        </Button>
                      </td>
                      <td>{q.version}</td>
                      <td>{dayjs(q.updated_at).format('DD.MM.YYYY HH:mm')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div
                  className="border rounded-bottom-3 rounded-top-0 bg-white p-3"
                  style={{ borderTop: 0 }}
                >
                  <Pagination className="justify-content-center mb-0">
                    <Pagination.First
                      disabled={page === 1}
                      onClick={() => setPage(1)}
                    />
                    <Pagination.Prev
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === page}
                        onClick={() => setPage(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={page === totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(p + 1, totalPages))
                      }
                    />
                    <Pagination.Last
                      disabled={page === totalPages}
                      onClick={() => setPage(totalPages)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </>
      )}

      <NewQuestionnaireModal
        show={showNewQuestionnaireModal}
        onHide={() => setShowNewQuestionnaireModal(false)}
      />
    </div>
  );
}
